import { supabase } from '../../lib/supabase';
import type { Event, Band, Profile } from '../../types/database';
import { calculateDistance } from '../../utils/distance';

export type EventWithDetails = Event & {
  band: Band;
  bands?: Band[];
  creator: Profile;
  distance?: number;
};

export async function fetchEvents(
  page: number,
  itemsPerPage: number,
  searchQuery?: string,
  startDate?: Date | null,
  endDate?: Date | null,
  coordinates?: { latitude: number; longitude: number } | null,
  nearMeEnabled?: boolean
) {
  try {
    let query = supabase
      .from('events')
      .select(`
        *,
        creator:profiles!events_created_by_fkey(*)
      `, { count: 'exact' });

    if (searchQuery && searchQuery.trim() !== '') {
      query = query.or(`title.ilike.%${searchQuery.trim()}%,description.ilike.%${searchQuery.trim()}%,location.ilike.%${searchQuery.trim()}%`);
    }

    if (startDate) {
      query = query.gte('event_date', startDate.toISOString());
    } else {
      query = query.gte('event_date', new Date().toISOString());
    }

    if (endDate) {
      query = query.lte('event_date', endDate.toISOString());
    }

    if (nearMeEnabled) {
      query = query.not('coordinates', 'is', null);
    }

    query = query.order('event_date', { ascending: true });

    const start = page * itemsPerPage;
    query = query.range(start, start + itemsPerPage - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    const eventsWithBands = await Promise.all((data || []).map(async (event) => {
      const { data: eventBands } = await supabase
        .from('event_bands')
        .select('band:bands(*)')
        .eq('event_id', event.id);

      const bands = eventBands?.map(eb => eb.band).filter(Boolean) || [];

      return {
        ...event,
        band: bands[0] || null,
        bands: bands as Band[],
      };
    }));

    const eventsWithDistance = eventsWithBands.map(event => {
      if (nearMeEnabled && coordinates && event.coordinates) {
        return {
          ...event,
          distance: calculateDistance(coordinates, event.coordinates),
        };
      }
      return event;
    });

    if (nearMeEnabled && coordinates) {
      eventsWithDistance.sort((a, b) => {
        if (a.coordinates && b.coordinates) {
          const distanceA = calculateDistance(coordinates, a.coordinates);
          const distanceB = calculateDistance(coordinates, b.coordinates);
          return distanceA - distanceB;
        }
        if (a.coordinates) return -1;
        if (b.coordinates) return 1;
        return 0;
      });
    }

    return { events: eventsWithDistance as EventWithDetails[], totalCount: count };
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
}

export async function fetchFeaturedEvents(
  limit: number = 5,
  coordinates?: { latitude: number; longitude: number } | null
) {
  try {
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        creator:profiles!events_created_by_fkey(*)
      `)
      .gte('event_date', new Date().toISOString())
      .order('event_date', { ascending: true })
      .limit(limit);

    if (error) throw error;

    const eventsWithBands = await Promise.all((data || []).map(async (event) => {
      const { data: eventBands } = await supabase
        .from('event_bands')
        .select('band:bands(*)')
        .eq('event_id', event.id);

      const bands = eventBands?.map(eb => eb.band).filter(Boolean) || [];

      return {
        ...event,
        band: bands[0] || null,
        bands: bands as Band[],
      };
    }));

    const eventsWithDistance = eventsWithBands.map(event => {
      if (coordinates && event.coordinates) {
        return {
          ...event,
          distance: calculateDistance(coordinates, event.coordinates),
        };
      }
      return event;
    });

    return eventsWithDistance as EventWithDetails[];
  } catch (error) {
    console.error('Error fetching featured events:', error);
    throw error;
  }
}